// 手机端展开长信息并让表单/操作换行，避免联系人、通知内容和相机错误提示超出屏幕；桌面保留原排布。
import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "./ui/sonner";
import { Plus, Trash2, Save, RotateCcw, CreditCard } from "lucide-react";

export default function TradePaymentPlans() {
  const { lang, api, user } = useApp();
  const zh = lang === "zh";
  const canEdit = ["admin", "eng_manager", "ceo"].includes(user.role);
  const [plans, setPlans] = useState([]);
  const [draft, setDraft] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/subcontract-trades/plans");
      setPlans(data || []);
      const d = {};
      (data || []).forEach((p) => { d[p.key] = p.milestones.map((m) => ({ ...m })); });
      setDraft(d);
    } catch (e) { toast.error(e?.response?.data?.detail || "加载失败"); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const setMs = (key, idx, field, val) => {
    setDraft((d) => {
      const arr = d[key].map((m, i) => (i === idx ? { ...m, [field]: field === "percent" ? val : val } : m));
      return { ...d, [key]: arr };
    });
  };
  const addMs = (key) => setDraft((d) => ({ ...d, [key]: [...d[key], { name: "", percent: 0 }] }));
  const rmMs = (key, idx) => setDraft((d) => ({ ...d, [key]: d[key].filter((_, i) => i !== idx) }));

  const sumOf = (key) => (draft[key] || []).reduce((s, m) => s + (Number(m.percent) || 0), 0);

  const save = async (key) => {
    const ms = (draft[key] || []).map((m) => ({ name: m.name, percent: Number(m.percent) || 0, ...(m.fixed != null ? { fixed: m.fixed } : {}), ...(m.balance ? { balance: true } : {}), ...(m.cap != null ? { cap: m.cap } : {}), ...(m.retention ? { retention: true } : {}) }));
    try {
      await api.put(`/subcontract-trades/${key}/plan`, { milestones: ms });
      toast.success(zh ? "付款模板已保存" : "Saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "保存失败"); }
  };
  const reset = async (key) => {
    try { await api.delete(`/subcontract-trades/${key}/plan`); toast.success(zh ? "已恢复默认" : "Reset"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "失败"); }
  };

  return (
    <div data-testid="trade-plans-panel">
      <p className="text-sm text-slate-500 mb-4">{zh ? "设置每个工种的默认付款节点与百分比。保存后，新建项目将自动预填该付款计划（各节点合计须为 100%）。" : "Set default payment milestones per trade; new projects pre-load them (must total 100%)."}</p>
      <div className="space-y-4">
        {plans.map((p) => {
          const sum = sumOf(p.key);
          const bad = Math.abs(sum - 100) > 0.5;
          return (
            <div key={p.key} data-testid={`plan-${p.key}`} className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-red-500" />{p.name}
                  {p.customized && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{zh ? "已自定义" : "custom"}</span>}
                </h3>
                <span className={`text-xs font-medium ${bad ? "text-red-600" : "text-emerald-600"}`} data-testid={`plan-sum-${p.key}`}>{zh ? "合计" : "Total"} {Math.round(sum * 100) / 100}%</span>
              </div>
              <div className="space-y-2">
                {(draft[p.key] || []).map((m, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input data-testid={`plan-${p.key}-name-${idx}`} value={m.name} disabled={!canEdit} onChange={(e) => setMs(p.key, idx, "name", e.target.value)} placeholder={zh ? "付款节点名称" : "Milestone name"} className="h-8 text-xs flex-1" />
                    <div className="flex items-center gap-1">
                      <Input data-testid={`plan-${p.key}-pct-${idx}`} type="number" value={m.percent} disabled={!canEdit} onChange={(e) => setMs(p.key, idx, "percent", e.target.value)} className="h-8 text-xs w-20" />
                      <span className="text-xs text-slate-400">%</span>
                    </div>
                    {canEdit && (draft[p.key] || []).length > 1 && (
                      <button data-testid={`plan-${p.key}-rm-${idx}`} onClick={() => rmMs(p.key, idx)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                ))}
              </div>
              {canEdit && (
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <Button data-testid={`plan-${p.key}-add`} size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => addMs(p.key)}><Plus className="h-3.5 w-3.5 mr-1" />{zh ? "加节点" : "Add"}</Button>
                  <div className="flex-1" />
                  {p.customized && <Button data-testid={`plan-${p.key}-reset`} size="sm" variant="outline" className="h-7 text-xs" onClick={() => reset(p.key)}><RotateCcw className="h-3.5 w-3.5 mr-1" />{zh ? "恢复默认" : "Reset"}</Button>}
                  <Button data-testid={`plan-${p.key}-save`} size="sm" disabled={bad} className="h-7 text-xs bg-brand-500 hover:bg-brand-600 text-white disabled:opacity-40" onClick={() => save(p.key)}><Save className="h-3.5 w-3.5 mr-1" />{zh ? "保存" : "Save"}</Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
